/**
 * Builds export configuration from grid column definitions.
 * Use this to keep export fields and labels in sync with the grid (single source of truth).
 *
 * - Only includes columns whose `field` exists on the data (excludes virtual/computed columns).
 * - Column labels come from each column's `title`.
 */

export type ColumnLike = {
    field: string;
    title?: string;
};

export type ExportConfig = {
    exportableFields: string[];
    columnLabels: Record<string, string>;
};

const DEFAULT_MODAL_STRINGS = {
    cancel: 'Cancel',
    export: 'Export',
    all_columns: 'All columns'
} as const;

/**
 * Derives exportable fields and column labels from column definitions and data.
 * Only includes fields that exist on the first data row (filters out virtual columns e.g. 'risk').
 */
export function getExportConfigFromColumns<T extends object>(
    columns: ColumnLike[],
    data: T[],
    options?: {
        /** Extra modal strings merged into columnLabels for the export modal (cancel, export, all_columns) */
        modalStrings?: Partial<Record<string, string>>;
        /** Field names to exclude from export even if present in data */
        excludeFields?: string[];
    }
): ExportConfig & { localization: Record<string, string> } {
    const excludeSet = new Set(options?.excludeFields ?? []);
    const firstRow = data[0] as object | undefined;

    const exportableFields = columns
        .filter(
            (col) =>
                col.field &&
                !excludeSet.has(col.field) &&
                firstRow !== undefined &&
                Object.prototype.hasOwnProperty.call(firstRow, col.field)
        )
        .map((col) => col.field);

    const columnLabels: Record<string, string> = {};
    exportableFields.forEach((field) => {
        const col = columns.find((c) => c.field === field);
        columnLabels[field] = col?.title ?? field;
    });

    const localization: Record<string, string> = {
        ...columnLabels,
        ...DEFAULT_MODAL_STRINGS,
        ...(options?.modalStrings ?? {})
    };

    return { exportableFields, columnLabels, localization };
}
